import { Link } from "react-router-dom";
import { Phone, AlertTriangle } from "lucide-react";
import { useApi } from "@/lib/api";
import { useSeo } from "@/lib/site";
import { ServiceCard } from "@/components/site/ServiceCard";

const BIG = [
  { n: "999", href: "tel:999", title: "Immediate danger", text: "Someone is unconscious, struggling to breathe, having a seizure, may have overdosed, is seriously injured, or anyone is at risk of harm." },
  { n: "111", href: "tel:111", title: "Urgent medical advice", text: "You need medical help or advice quickly, but it isn't life-threatening, or you're unsure what to do." },
  { n: "116 123", href: "tel:116123", title: "Samaritans", text: "Free, confidential emotional support, 24 hours a day, for anyone who is struggling to cope." },
];

export default function UrgentHelp() {
  useSeo("Urgent Help", "Addiction & Us is not an emergency service. If someone is in immediate danger in the UK, call 999.");
  const { data } = useApi("/directory?type=emergency");
  const { data: all } = useApi("/directory");
  const more = (all || []).filter((s) => s.urgent && !["emergency-999", "nhs-111", "samaritans"].includes(s.slug));
  return (
    <div className="container-site pt-12" data-testid="urgent-help-page">
      <div className="rounded-[2rem] bg-clay-light border border-clay/25 p-6 sm:p-10 flex gap-5">
        <AlertTriangle className="h-8 w-8 text-clay-dark shrink-0" aria-hidden="true" />
        <div>
          <h1 className="text-4xl sm:text-5xl">Urgent help</h1>
          <p className="mt-4 text-lg max-w-3xl" data-testid="not-emergency-statement"><strong>Addiction &amp; Us is not an emergency service.</strong> We don't monitor messages in real time and we can't respond to a crisis. If you or someone else needs help now, please contact one of the services below.</p>
        </div>
      </div>
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {BIG.map((b) => (
          <a key={b.n} href={b.href} className="group card-soft !bg-white p-7 flex flex-col gap-3 hover:border-teal transition-colors" data-testid={`urgent-call-${b.n.replace(/\s/g, "")}`}>
            <span className="inline-flex items-center gap-2 text-teal font-semibold"><Phone className="h-5 w-5" aria-hidden="true" />Call</span>
            <span className="font-serif text-5xl">{b.n}</span>
            <h2 className="text-xl font-semibold font-sans">{b.title}</h2>
            <p className="text-ink-soft">{b.text}</p>
          </a>
        ))}
      </div>
      <section className="mt-12 grid gap-8 lg:grid-cols-2">
        <div className="card-soft p-6 md:p-8">
          <h2 className="text-2xl">If someone may have overdosed</h2>
          <ul className="mt-4 space-y-2 text-ink-soft list-disc pl-5">
            <li>Call 999 straight away and follow the call handler's instructions.</li>
            <li>If they are unconscious but breathing, place them in the recovery position.</li>
            <li>Stay with them if it is safe to do so.</li>
            <li>Tell paramedics anything you know about what they have taken.</li>
          </ul>
        </div>
        <div className="card-soft p-6 md:p-8">
          <h2 className="text-2xl">If you feel unsafe at home</h2>
          <p className="mt-4 text-ink-soft">If you are in danger, call 999. If you can't speak, call 999 and then press 55 when prompted on a mobile. This is the Silent Solution, which lets the police know it's a genuine emergency. The National Domestic Abuse Helpline is available 24 hours a day.</p>
          <p className="mt-3 text-sm text-ink-muted">Use the "Quick exit" button at the top of every page to leave this site quickly. It takes you to BBC Weather, but it can't erase your browser history or guarantee privacy. On a shared device, consider using a private browsing window and clearing your history.</p>
        </div>
      </section>
      <section className="mt-12" aria-labelledby="more-urgent-heading">
        <h2 id="more-urgent-heading" className="text-3xl mb-6">More support available now</h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{[...(data || []).filter((s) => s.slug !== "emergency-999"), ...more].map((s) => <ServiceCard key={s.slug} entry={s} />)}</div>
        <p className="mt-8 text-ink-soft">For non-urgent support, see our <Link to="/directory" className="btn-ghost !inline">support directory</Link>.</p>
      </section>
    </div>
  );
}
